<template>
  <div class="settings-page">
    <div v-if="activeTab !== 'encryption-keys'" class="toolbar">
      <Button :icon="Plus" plain class="btn" @click="openCreate">
        {{ createButtonLabel }}
      </Button>

      <div class="filters">
        <el-input
          v-model="search"
          clearable
          :placeholder="searchPlaceholder"
          class="search-input"
        >
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
        </el-input>

        <el-select
          v-model="statusFilter"
          class="status-filter"
          placeholder="Status"
        >
          <el-option label="Todos" value="all" />
          <el-option label="Ativos" value="active" />
          <el-option label="Inativos" value="inactive" />
        </el-select>
      </div>
    </div>

    <el-tabs v-model="activeTab" class="settings-tabs">
      <el-tab-pane label="Identificador de Cliente" name="download-window">
        <div class="table-wrap">
          <el-table
            :data="paginatedRows"
            v-loading="loading"
            empty-text="Nenhuma configuração encontrada"
          >
            <el-table-column prop="download_window" label="Código do Cliente" min-width="180" />
            <el-table-column prop="business_client" label="Cliente" min-width="150" />
            <el-table-column prop="download_window_hex" label="Hex" min-width="140" />
            <el-table-column label="Status" width="120">
              <template #default="{ row }">
                <el-tag :type="row.active ? 'success' : 'info'" effect="light">
                  {{ row.active ? "Ativo" : "Inativo" }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="updatedAtLabel" label="Atualizado em" min-width="170" />
            <el-table-column label="Ações" width="150" fixed="right">
              <template #default="{ row }">
                <div class="row-actions">
                  <el-tooltip content="Visualizar" placement="top">
                    <el-button circle size="small" @click="openView(row)">
                      <el-icon><View /></el-icon>
                    </el-button>
                  </el-tooltip>
                  <el-tooltip content="Editar" placement="top">
                    <el-button circle size="small" @click="openEdit(row)">
                      <el-icon><Edit /></el-icon>
                    </el-button>
                  </el-tooltip>
                  <el-tooltip content="Inativar" placement="top">
                    <el-button
                      circle
                      size="small"
                      type="danger"
                      :disabled="!row.active"
                      @click="confirmDeactivate(row)"
                    >
                      <el-icon><Delete /></el-icon>
                    </el-button>
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-tab-pane>

      <el-tab-pane label="Cenários" name="scenarios">
        <div class="table-wrap">
          <el-table
            :data="paginatedRows"
            v-loading="loading"
            empty-text="Nenhum cenário encontrado"
          >
            <el-table-column prop="download_scenario" label="Cenário" width="110" />
            <el-table-column prop="business_client" label="Cliente" min-width="150" />
            <el-table-column prop="description" label="Descrição" min-width="220" show-overflow-tooltip>
              <template #default="{ row }">
                {{ row.description || "-" }}
              </template>
            </el-table-column>
            <el-table-column label="Status" width="120">
              <template #default="{ row }">
                <el-tag :type="row.active ? 'success' : 'info'" effect="light">
                  {{ row.active ? "Ativo" : "Inativo" }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="updatedAtLabel" label="Atualizado em" min-width="170" />
            <el-table-column label="Ações" width="150" fixed="right">
              <template #default="{ row }">
                <div class="row-actions">
                  <el-tooltip content="Visualizar" placement="top">
                    <el-button circle size="small" @click="openView(row)">
                      <el-icon><View /></el-icon>
                    </el-button>
                  </el-tooltip>
                  <el-tooltip content="Editar" placement="top">
                    <el-button circle size="small" @click="openEdit(row)">
                      <el-icon><Edit /></el-icon>
                    </el-button>
                  </el-tooltip>
                  <el-tooltip content="Inativar" placement="top">
                    <el-button
                      circle
                      size="small"
                      type="danger"
                      :disabled="!row.active"
                      @click="confirmDeactivate(row)"
                    >
                      <el-icon><Delete /></el-icon>
                    </el-button>
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-tab-pane>
      <el-tab-pane label="Chaves de criptografia" name="encryption-keys" lazy>
        <EncryptionKeysSettings :business-clients="effectiveClients" />
      </el-tab-pane>
    </el-tabs>

    <div v-if="activeTab !== 'encryption-keys'" class="pagination-wrap">
      <span class="pagination-count">{{ paginationLabel }}</span>
      <el-select
        :model-value="pageSize"
        placement="top-end"
        class="page-size-select app-select"
        @change="handlePageSizeChange"
      >
        <el-option
          v-for="size in [10, 20, 50, 100]"
          :key="size"
          :label="`${size}/página`"
          :value="size"
        />
      </el-select>
      <el-pagination
        :current-page="currentPage"
        :page-size="pageSize"
        :total="filteredRows.length"
        layout="prev, pager, next"
        background
        @current-change="currentPage = $event"
      />
    </div>

    <Card
      v-if="showModal"
      :title="modalTitle"
      width="min(620px, 92vw)"
      @close="closeModal"
    >
      <el-form
        ref="formRef"
        :model="form"
        :rules="rules"
        label-position="top"
        class="config-form"
        @submit.prevent
      >
        <el-form-item label="Cliente" prop="business_client">
          <el-select
            v-if="canChooseEffectiveBusinessClient"
            v-model="form.business_client"
            :disabled="isReadOnly || isEditing"
            :loading="loadingClients"
            filterable
            placeholder="Selecione o cliente"
            class="client-select"
          >
            <el-option
              v-for="client in effectiveClients"
              :key="client"
              :label="client"
              :value="client"
            />
          </el-select>
          <el-input
            v-else
            v-model="form.business_client"
            disabled
            placeholder="Cliente identificado pelo domínio"
          />
        </el-form-item>

        <div class="form-grid">
          <el-form-item
            v-if="activeTab === 'scenarios'"
            label="Cenário"
            prop="download_scenario"
          >
            <el-input-number
              v-model="form.download_scenario"
              :disabled="isReadOnly || isEditing"
              :min="0"
              :max="maxUint32"
              controls-position="right"
              class="number-field"
            />
          </el-form-item>

          <el-form-item
            v-if="activeTab === 'download-window'"
            label="Identificador de Cliente"
            prop="download_window"
          >
            <el-input-number
              v-model="form.download_window"
              :disabled="isReadOnly"
              :min="1"
              :max="maxUint32"
              controls-position="right"
              class="number-field"
            />
          </el-form-item>
        </div>

        <el-form-item
          v-if="activeTab === 'scenarios'"
          label="Descrição"
          prop="description"
        >
          <el-input
            v-model="form.description"
            :disabled="isReadOnly"
            type="textarea"
            :rows="3"
            maxlength="255"
            show-word-limit
            placeholder="Informe uma descrição para o cenário"
          />
        </el-form-item>

        <el-form-item label="Ativo">
          <el-switch
            v-model="form.active"
            :disabled="isReadOnly"
            active-text="Sim"
            inactive-text="Não"
          />
        </el-form-item>

        <div v-if="selectedRow" class="metadata">
          <span v-if="selectedRow.download_window_hex">
            Hex: {{ selectedRow.download_window_hex }}
          </span>
          <span>Criado em: {{ formatDate(selectedRow.created_at) }}</span>
          <span>Atualizado em: {{ formatDate(selectedRow.updated_at) }}</span>
        </div>

        <div class="form-actions">
          <el-button @click="closeModal">
            {{ isReadOnly ? "Fechar" : "Cancelar" }}
          </el-button>
          <el-button
            v-if="!isReadOnly"
            type="primary"
            :loading="saving"
            @click="submitForm"
          >
            Salvar
          </el-button>
        </div>
      </el-form>
    </Card>
  </div>
</template>

<script>
import axios from "axios";
import { ElMessage, ElMessageBox } from "element-plus";
import {
  Delete,
  Edit,
  Plus,
  Search,
  View,
} from "@element-plus/icons-vue";
import Button from "../Component/Button.vue";
import Card from "../Component/Card.vue";
import EncryptionKeysSettings from "./EncryptionKeysSettings.vue";
import { config } from "../../config";
import { loadBusinessClients } from "../../services/businessClients";
import { clientViewState } from "../../utils/clientView";

const MAX_UINT32 = 0xFFFFFFFF;

function emptyForm() {
  return {
    business_client: "",
    download_scenario: 0,
    download_window: 1,
    description: "",
    active: true,
  };
}

export default {
  name: "DownloadWindowSettings",
  components: { Button, Card, Delete, Edit, Search, View, EncryptionKeysSettings },
  data() {
    return {
      windowRows: [],
      scenarioRows: [],
      // Permite chegar direto numa aba específica via link (ex.: /settings?tab=scenarios).
      activeTab: ["scenarios", "encryption-keys"].includes(this.$route.query.tab) ? this.$route.query.tab : "download-window",
      search: "",
      statusFilter: "all",
      currentPage: 1,
      pageSize: 20,
      loading: false,
      loadingClients: false,
      saving: false,
      businessClient: null,
      canChooseBusinessClient: false,
      availableClients: [],
      clientViewState,
      showModal: false,
      mode: "create",
      selectedRow: null,
      form: emptyForm(),
      maxUint32: MAX_UINT32,
      rules: {
        business_client: [
          { required: true, message: "Informe o cliente", trigger: "change" },
          { min: 2, max: 55, message: "Use entre 2 e 55 caracteres", trigger: "change" },
        ],
        download_scenario: [
          { required: true, message: "Informe o cenário", trigger: "change" },
          { type: "number", min: 0, max: MAX_UINT32, message: "Valor fora do range permitido", trigger: "change" },
        ],
        download_window: [
          { required: true, message: "Informe o identificador de cliente", trigger: "change" },
          { type: "number", min: 1, max: MAX_UINT32, message: "Valor fora do range permitido", trigger: "change" },
        ],
        description: [
          { max: 255, message: "Use até 255 caracteres", trigger: "change" },
        ],
      },
    };
  },
  computed: {
    Plus: () => Plus,
    searchPlaceholder() {
      return this.activeTab === "scenarios"
        ? "Filtrar por cliente ou cenário"
        : "Filtrar por cliente ou identificador de cliente";
    },
    createButtonLabel() {
      return this.activeTab === "scenarios"
        ? "Novo cenário"
        : "Novo identificador";
    },
    modalTitle() {
      if (this.mode === "view") return "Visualizar configuração";
      if (this.mode === "edit") return "Editar configuração";
      return this.activeTab === "scenarios"
        ? "Novo cenário"
        : "Novo Identificador de Cliente";
    },
    isEditing() {
      return this.mode === "edit";
    },
    isReadOnly() {
      return this.mode === "view";
    },
    effectiveClients() {
      return this.clientViewState.selectedClient
        ? [this.clientViewState.selectedClient]
        : this.availableClients;
    },
    effectiveBusinessClient() {
      return this.effectiveClients.length === 1 ? this.effectiveClients[0] : null;
    },
    canChooseEffectiveBusinessClient() {
      return this.effectiveClients.length > 1;
    },
    filteredRows() {
      const term = this.search.trim().toLowerCase();
      const sourceRows = this.activeTab === "scenarios"
        ? this.scenarioRows
        : this.windowRows;

      return sourceRows.filter((row) => {
        if (
          this.effectiveBusinessClient &&
          row.business_client !== this.effectiveBusinessClient
        ) {
          return false;
        }

        const matchesStatus =
          this.statusFilter === "all" ||
          (this.statusFilter === "active" && row.active) ||
          (this.statusFilter === "inactive" && !row.active);

        if (!matchesStatus) return false;
        if (!term) return true;

        return [
          row.business_client,
          row.download_scenario,
          row.download_window,
          row.download_window_hex,
          row.description,
        ]
          .join(" ")
          .toLowerCase()
          .includes(term);
      });
    },
    paginatedRows() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredRows.slice(start, start + this.pageSize);
    },
    paginationLabel() {
      const total = this.filteredRows.length;
      if (!total) return "0 resultados";

      const start = (this.currentPage - 1) * this.pageSize + 1;
      const end = Math.min(start + this.pageSize - 1, total);
      return `${start}-${end} de ${total} resultados`;
    },
  },
  watch: {
    activeTab() {
      this.currentPage = 1;
    },
    search() {
      this.currentPage = 1;
    },
    statusFilter() {
      this.currentPage = 1;
    },
    "clientViewState.selectedClient"() {
      this.currentPage = 1;
      if (this.showModal) this.closeModal();
      this.fetchRows();
    },
  },
  methods: {
    handlePageSizeChange(newSize) {
      this.pageSize = newSize;
      this.currentPage = 1;
    },
    formatDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleString("pt-BR");
    },
    normalizeRow(row) {
      return {
        ...row,
        updatedAtLabel: this.formatDate(row.updated_at || row.created_at),
      };
    },
    mergeAvailableClients(rows = []) {
      const fromRows = rows
        .map((item) => item.business_client)
        .filter(Boolean);
      const merged = [...this.availableClients, ...fromRows];
      this.availableClients = [...new Set(merged)]
        .filter((name) => name !== "globo")
        .sort((a, b) => a.localeCompare(b));
    },
    extractErrorMessage(error) {
      const detail = error?.response?.data?.detail;
      if (Array.isArray(detail)) {
        return "Verifique os campos informados.";
      }
      return detail || "Não foi possível concluir a operação.";
    },
    async fetchRows() {
      this.loading = true;
      try {
        const [windowsResponse, scenariosResponse] = await Promise.all([
          axios.get(`${config.API}/v1/download-configurations/`),
          axios.get(`${config.API}/v1/download-configurations/scenarios/`),
        ]);

        this.windowRows = (windowsResponse.data || []).map(this.normalizeRow);
        this.scenarioRows = (scenariosResponse.data || []).map(this.normalizeRow);
        if (this.canChooseEffectiveBusinessClient) {
          this.mergeAvailableClients([
            ...this.windowRows,
            ...this.scenarioRows,
          ]);
        }
      } catch (error) {
        ElMessage.error(this.extractErrorMessage(error));
      } finally {
        this.loading = false;
      }
    },
    async fetchAvailableClients() {
      this.loadingClients = true;
      try {
        this.availableClients = await loadBusinessClients();
        this.canChooseBusinessClient = this.availableClients.length > 1;
        this.businessClient = this.availableClients.length === 1
          ? this.availableClients[0]
          : null;
      } catch (error) {
        ElMessage.error("Não foi possível carregar os clientes.");
      } finally {
        this.loadingClients = false;
      }
    },
    openCreate() {
      this.mode = "create";
      this.selectedRow = null;
      this.form = {
        ...emptyForm(),
        business_client: this.canChooseEffectiveBusinessClient
          ? ""
          : this.effectiveBusinessClient,
      };
      this.showModal = true;
      document.body.style.overflow = "hidden";
    },
    openView(row) {
      this.mode = "view";
      this.selectedRow = row;
      this.form = {
        business_client: row.business_client,
        download_scenario: row.download_scenario,
        download_window: row.download_window,
        description: row.description || "",
        active: row.active,
      };
      this.showModal = true;
      document.body.style.overflow = "hidden";
    },
    openEdit(row) {
      this.mode = "edit";
      this.selectedRow = row;
      this.form = {
        business_client: row.business_client,
        download_scenario: row.download_scenario,
        download_window: row.download_window,
        description: row.description || "",
        active: row.active,
      };
      this.showModal = true;
      document.body.style.overflow = "hidden";
    },
    closeModal() {
      this.showModal = false;
      this.selectedRow = null;
      this.form = emptyForm();
      document.body.style.overflow = "";
    },
    buildPayload() {
      const payload = {
        active: Boolean(this.form.active),
      };

      if (this.activeTab === "download-window") {
        payload.download_window = Number(this.form.download_window);
      }

      if (this.mode === "create") {
        payload.business_client = this.form.business_client.trim().toLowerCase();
      }

      if (this.mode === "create" && this.activeTab === "scenarios") {
        payload.download_scenario = Number(this.form.download_scenario || 0);
      }

      if (this.activeTab === "scenarios") {
        payload.description = this.form.description?.trim() || null;
      }

      return payload;
    },
    async submitForm() {
      await this.$refs.formRef.validate(async (valid) => {
        if (!valid) return;

        this.saving = true;
        try {
          const payload = this.buildPayload();
          if (this.mode === "create") {
            const url = this.activeTab === "scenarios"
              ? `${config.API}/v1/download-configurations/scenarios/`
              : `${config.API}/v1/download-configurations/`;
            await axios.post(url, payload);
            ElMessage.success("Configuração criada com sucesso.");
          } else {
            const url = this.activeTab === "scenarios"
              ? `${config.API}/v1/download-configurations/${this.selectedRow.business_client}/scenario/${this.selectedRow.download_scenario}`
              : `${config.API}/v1/download-configurations/${this.selectedRow.business_client}`;
            await axios.put(url, payload);
            ElMessage.success("Configuração atualizada com sucesso.");
          }

          this.closeModal();
          await this.fetchRows();
        } catch (error) {
          ElMessage.error(this.extractErrorMessage(error));
        } finally {
          this.saving = false;
        }
      });
    },
    async confirmDeactivate(row) {
      try {
        await ElMessageBox.confirm(
          this.activeTab === "scenarios"
            ? `Deseja inativar o cenário ${row.download_scenario} de ${row.business_client}?`
            : `Deseja inativar o identificador de cliente de ${row.business_client}?`,
          "Inativar configuração",
          {
            confirmButtonText: "Inativar",
            cancelButtonText: "Cancelar",
            type: "warning",
          },
        );

        const url = this.activeTab === "scenarios"
          ? `${config.API}/v1/download-configurations/${row.business_client}/scenario/${row.download_scenario}`
          : `${config.API}/v1/download-configurations/${row.business_client}`;
        await axios.delete(url);
        ElMessage.success("Configuração inativada com sucesso.");
        await this.fetchRows();
      } catch (error) {
        if (error === "cancel") return;
        ElMessage.error(this.extractErrorMessage(error));
      }
    },
  },
  async mounted() {
    await this.fetchAvailableClients();
    await this.fetchRows();
  },
  beforeUnmount() {
    document.body.style.overflow = "";
  },
};
</script>

<style scoped>
@import "@/assets/css/fonts.css";

.settings-page {
  font-family: "Globo-Regular", sans-serif;
}

.settings-page :deep(.el-button),
.settings-page :deep(.el-form-item__label),
.settings-page :deep(.el-input__inner),
.settings-page :deep(.el-select__placeholder),
.settings-page :deep(.el-table),
.settings-page :deep(.el-tag),
.settings-page :deep(.el-pagination) {
  font-family: "Globo-Regular", sans-serif;
}

:global(.el-popper),
:global(.el-popper__content),
:global(.el-message-box),
:global(.el-message-box__title),
:global(.el-message-box__content),
:global(.el-message-box__message),
:global(.el-message-box__btns .el-button) {
  font-family: "Globo-Regular", sans-serif;
}

:global(.el-message-box__title) {
  font-family: "Globo-Bold", "Globo-Regular", sans-serif;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.toolbar :deep(.el-button.is-plain) {
  color: #3a6d8c;
  font-weight: 600;
}

.toolbar :deep(.el-button.is-plain .el-icon) {
  color: #3a6d8c;
}

.filters {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.search-input {
  width: min(360px, 72vw);
}

.status-filter {
  width: 150px;
}

.settings-tabs {
  margin-top: 4px;
}

.settings-tabs :deep(.el-tabs__item) {
  font-family: "Globo-Regular", sans-serif;
  color: #607182;
}

.settings-tabs :deep(.el-tabs__item.is-active) {
  color: #3a6d8c;
  font-family: "Globo-Bold", "Globo-Regular", sans-serif;
}

.settings-tabs :deep(.el-tabs__active-bar) {
  background-color: #3a6d8c;
}

.table-wrap {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.row-actions {
  display: flex;
  gap: 8px;
}

.pagination-wrap {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 14px;
  flex-wrap: wrap;
}

.pagination-count {
  color: #607182;
  font-size: 13px;
  margin-right: auto;
}

.app-select :deep(.el-select__wrapper) {
  min-height: 40px;
  box-shadow: 0 0 0 1px #dcdfe6 inset;
}

.app-select :deep(.el-select__wrapper.is-hovering:not(.is-focused)),
.app-select :deep(.el-select__wrapper.is-focused) {
  box-shadow: 0 0 0 1px #3a6d8c inset;
}

.app-select :deep(.el-select__placeholder) {
  color: #2b4054;
}

.app-select :deep(.el-select__placeholder.is-transparent) {
  color: #9aa4af;
}

.app-select :deep(.el-select__caret) {
  color: #9aa4af;
}

.page-size-select {
  width: 110px;
}

.pagination-wrap :deep(.el-pagination) {
  --el-color-primary: #3a6d8c;
  --el-pagination-hover-color: #3a6d8c;
}

.pagination-wrap :deep(.el-pagination.is-background .el-pager li.is-active),
.pagination-wrap :deep(.el-pagination.is-background .el-pager li.is-active:hover) {
  background-color: #3a6d8c !important;
  color: #fff !important;
}

.config-form {
  color: #2b4054;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.number-field,
.client-select {
  width: 100%;
}

.metadata {
  display: grid;
  gap: 6px;
  margin: 6px 0 18px;
  color: #607182;
  font-size: 13px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 10px;
}

@media (max-width: 720px) {
  .toolbar {
    align-items: stretch;
  }

  .filters,
  .search-input,
  .status-filter {
    width: 100%;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .pagination-wrap {
    align-items: stretch;
  }
}
</style>
